// src/components/stock/CodesSearchPannel.tsx
//
// CodesSearchPannel
// - Presentation-only search UI for Stock Codes page.
// - Delegates all state ownership to the parent page.
// - Uses shared SearchPanel for:
//   - Search / Reset buttons
//   - Optional autoSearch (pass-through only)
//   - Active chips (with readable labels)
//   - Header actions (Export)
//
// IMPORTANT (policy):
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel owns global defaults via constants.
//   This component must pass through `autoSearch` only if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, FormControlLabel, Grid, Checkbox as MuiCheckbox } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SearchPanelAction,
  type SetSearchValueFn,
} from '@components/common/search';

import { StockOrder, type GetStocksParams } from '@apis/stock';
import { StockStatus } from '@models/stock';

type SearchForm = GetStocksParams;

type Option<T> = { label: string; value: T };

export type CodesSearchPannelProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;

  // Draft state (owned by page)
  draftSearch: SearchForm;
  setValue: SetSearchValueFn<SearchForm>;

  // Options + selected values (owned by page)
  regionOptions: Array<Option<number>>;
  supplierOptions: Array<Option<number>>;
  statusOptions: Array<Option<StockStatus>>;

  selectedRegion: Option<number> | null;
  selectedSupplier: Option<number> | null;
  selectedStatus: Option<StockStatus> | null;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  // Export
  onExport: () => void;
  exportLoading?: boolean;
  exportDisabled?: boolean;

  // Pass-through only
  autoSearch?: { enabled?: boolean; delayMs?: number };
};

const CodesSearchPannel: React.FC<CodesSearchPannelProps> = ({
  open,
  onOpenChange,

  draftSearch,
  setValue,

  regionOptions,
  supplierOptions,
  statusOptions,

  selectedRegion,
  selectedSupplier,
  selectedStatus,

  onSearch,
  onReset,

  onExport,
  exportLoading = false,
  exportDisabled = false,

  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Lookup maps (O(1) chip label access)                                */
  /* ------------------------------------------------------------------ */

  const regionLabelById = useMemo(() => {
    const map = new Map<number, string>();
    regionOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [regionOptions]);

  const supplierLabelById = useMemo(() => {
    const map = new Map<number, string>();
    supplierOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [supplierOptions]);

  const statusLabelByValue = useMemo(() => {
    const map = new Map<StockStatus, string>();
    statusOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [statusOptions]);

  /* ------------------------------------------------------------------ */
  /* Chips                                                              */
  /* ------------------------------------------------------------------ */

  const chips: Array<SearchChipSpec<SearchForm>> = useMemo(
    () => [
      {
        key: 'stockCode',
        label: 'Stock Code',
        isActive: (s) => Boolean(String((s as any).stockCode ?? '').trim()),
        getValueText: (s) => String((s as any).stockCode ?? '').trim(),
        clear: ({ setValue }) => setValue('stockCode' as any, '' as any),
      },
      {
        key: 'stockName',
        label: 'Stock Name',
        isActive: (s) => Boolean(String((s as any).stockName ?? '').trim()),
        getValueText: (s) => String((s as any).stockName ?? '').trim(),
        clear: ({ setValue }) => setValue('stockName' as any, '' as any),
      },
      {
        key: 'regionId',
        label: 'Region',
        isActive: (s) => typeof (s as any).regionId === 'number',
        getValueText: (s) => {
          const id = (s as any).regionId;
          if (typeof id !== 'number') return '';
          return regionLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('regionId' as any, null as any),
      },
      {
        key: 'supplierId',
        label: 'Supplier',
        isActive: (s) => typeof (s as any).supplierId === 'number',
        getValueText: (s) => {
          const id = (s as any).supplierId;
          if (typeof id !== 'number') return '';
          return supplierLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('supplierId' as any, null as any),
      },
      {
        key: 'status',
        label: 'Status',
        isActive: (s) => Boolean((s as any).status),
        getValueText: (s) => {
          const v = (s as any).status as StockStatus | undefined;
          if (!v) return '';
          return statusLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('status' as any, null as any),
      },
      {
        key: 'ordering',
        label: 'Reorder Required',
        isActive: (s) => (s as any).ordering === StockOrder.REORDER_REQUIRED,
        getValueText: () => 'Yes',
        clear: ({ setValue }) => setValue('ordering' as any, null as any),
      },
    ],
    [regionLabelById, supplierLabelById, statusLabelByValue],
  );

  const chipsConfig: ActiveChipsConfig<SearchForm> = useMemo(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      // Chip removal should immediately apply a search (consistent across pages).
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  /* ------------------------------------------------------------------ */
  /* Actions                                                            */
  /* ------------------------------------------------------------------ */

  const actions: SearchPanelAction[] = useMemo(
    () => [
      {
        key: 'export',
        label: 'Export',
        startIcon: <FileDownloadIcon />,
        variant: 'outlined',
        onClick: onExport,
        disabled: exportDisabled || exportLoading,
      },
    ],
    [exportDisabled, exportLoading, onExport],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  const reorderRequired = draftSearch.ordering === StockOrder.REORDER_REQUIRED;

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      actions={actions}
      autoSearch={autoSearch} // pass-through only
    >
      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Stock Code"
            placeholder="Enter the stock code"
            size="small"
            fullWidth
            value={String(draftSearch.stockCode ?? '')}
            onChangeValue={(v) => setValue('stockCode' as any, v as any)}
            onClear={() => setValue('stockCode' as any, '' as any)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            label="Stock Name"
            placeholder="Enter the stock name"
            size="small"
            fullWidth
            value={String(draftSearch.stockName ?? '')}
            onChangeValue={(v) => setValue('stockName' as any, v as any)}
            onClear={() => setValue('stockName' as any, '' as any)}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<number>, false, false, false>
            disablePortal
            options={regionOptions}
            value={selectedRegion}
            onChange={(_e, v) => setValue('regionId' as any, (v?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField
                {...params}
                label="Region"
                size="small"
                fullWidth
                placeholder="Select the region"
                onClear={() => setValue('regionId' as any, null as any)}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<number>, false, false, false>
            disablePortal
            options={supplierOptions}
            value={selectedSupplier}
            onChange={(_e, v) => setValue('supplierId' as any, (v?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField
                {...params}
                label="Supplier"
                size="small"
                fullWidth
                placeholder="Select the supplier"
                onClear={() => setValue('supplierId' as any, null as any)}
              />
            )}
          />
        </Grid>

        {/* Row 3 */}
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete<Option<StockStatus>, false, false, false>
            disablePortal
            options={statusOptions}
            value={selectedStatus}
            onChange={(_e, v) => setValue('status' as any, (v?.value ?? null) as any)}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            renderInput={(params) => (
              <SearchTextField
                {...params}
                label="Status"
                size="small"
                fullWidth
                placeholder="Select the status"
                onClear={() => setValue('status' as any, null as any)}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <MuiCheckbox
                checked={reorderRequired}
                onChange={(_, checked) =>
                  setValue('ordering' as any, checked ? StockOrder.REORDER_REQUIRED : null)
                }
              />
            }
            label="Reorder Required"
          />
        </Grid>
      </Grid>
    </SearchPanel>
  );
};

export default CodesSearchPannel;
